import { gradeJumpClearance } from '../../jumpChart.js';
import { GOOD_SCORE, PERFECT_SCORE } from '../../gameTypes.js';
import { comboMultiplier, computeAccuracy } from '../../scoring.js';
import {
  surfaceSegmentAt,
  type JumpDeathReason,
  type JumpEngineConfig,
  type JumpEngineState,
  type JumpPassVerdict,
  type JumpResolution,
} from './state.js';

export type JumpEngineInputType = 'down' | 'up';

/** One captured Jump input — a discrete press/release edge. */
export interface JumpEngineInput {
  beat: number;
  type: JumpEngineInputType;
}

/**
 * Events the engine emits, wired to renderer FX, audio SFX, and the shared
 * `LoopRunState` star/cup tracker.
 *   - `edge`: input press/release — trail / button visuals.
 *   - `jump`: cube launched off a surface (`auto` = triggered by holding
 *     through a landing rather than a fresh press) — squash + SFX.
 *   - `land`: cube touched down on a surface — dust puff + SFX.
 *   - `obstacle_passed`: cleared an obstacle alive — floating score + combo.
 *   - `death`: collided or fell into a pit — screen-shake, fail FX, audio cut.
 *   - `completed`: a full loop cleared without dying.
 */
export type JumpEngineEvent =
  | { type: 'edge'; beat: number; payload: { pressing: boolean; cubeY: number } }
  | { type: 'jump'; beat: number; payload: { cubeY: number; auto: boolean } }
  | { type: 'land'; beat: number; payload: { cubeY: number; surfaceTopY: number } }
  | {
      type: 'obstacle_passed';
      beat: number;
      payload: {
        obstacleId: string;
        scoreDelta: number;
        combo: number;
        verdict: JumpPassVerdict;
      };
    }
  | { type: 'death'; beat: number; payload: { obstacleId: string; reason: JumpDeathReason } }
  | { type: 'completed'; beat: number };

export interface JumpStepInput {
  config: JumpEngineConfig;
  state: JumpEngineState;
  fromBeat: number;
  toBeat: number;
  inputs: JumpEngineInput[];
}

export interface JumpStepOutput {
  state: JumpEngineState;
  events: JumpEngineEvent[];
}

/**
 * Defensive cap on events processed within a single `step()` window. The
 * trajectory is closed-form and every event strictly advances the beat
 * cursor (a jump always precedes its landing by the air-time), so a healthy
 * chart resolves a window in a handful of iterations. This bars a pathological
 * chart (e.g. a degenerate near-zero air-time that slipped validation) from
 * spinning forever.
 */
const JUMP_MAX_STEP_EVENTS = 100000;

const EPS = 1e-9;

/**
 * Cube CENTRE Y at `beat` from the current anchor. Grounded: the surface
 * holds it at `anchorY` (constant until the next event re-anchors).
 * Airborne: the parabola `anchorY + anchorVy·dt + ½·g·dt²`.
 *
 * Does NOT mutate state. Re-anchoring the airborne parabola at any beat is
 * algebraically exact (`y0+v0·(a+b)+½g·(a+b)² == y1+v1·b+½g·b²`), so sampling
 * from the last EVENT anchor — which lands on beats identical across client
 * (per-frame) and server (1/16-beat) re-sims — is resolution-independent.
 */
export function cubeCentreYAt(
  state: JumpEngineState,
  config: JumpEngineConfig,
  beat: number,
): number {
  if (state.grounded) {
    return state.anchorY;
  }
  const dt = beat - state.anchorBeat;
  if (dt <= 0) {
    return state.anchorY;
  }
  return state.anchorY + state.anchorVy * dt + 0.5 * config.gravity * dt * dt;
}

/** Airborne vertical velocity (+down) at `beat`. Grounded is always 0. */
function cubeVyAt(state: JumpEngineState, config: JumpEngineConfig, beat: number): number {
  if (state.grounded) {
    return 0;
  }
  return state.anchorVy + config.gravity * (beat - state.anchorBeat);
}

export interface JumpLandingEvent {
  type: 'landing';
  beat: number;
  surfaceTopY: number;
  /** True when the landing surface is a pit floor — a fall-death, not a touchdown. */
  lethal: boolean;
}

export interface JumpWalkOffEvent {
  type: 'walkoff';
  beat: number;
}

/**
 * Earliest beat in `(fromBeat, toBeat]` where the descending arc meets a
 * surface segment's top from above. Walks segments forward from the anchor;
 * the cube has already cleared every leading-face hazard (checked separately)
 * so the first crossing is a legal landing. Returns null if it stays airborne
 * through the whole window.
 */
function computeNextLanding(
  state: JumpEngineState,
  config: JumpEngineConfig,
  fromBeat: number,
  toBeat: number,
): JumpLandingEvent | null {
  const segs = config.surface;
  const g = config.gravity;
  const half = config.playerHalf;
  const scanStart = Math.max(fromBeat, state.anchorBeat);
  let i = segs.indexOf(surfaceSegmentAt(segs, scanStart));
  for (; i < segs.length; i += 1) {
    const seg = segs[i];
    const winStart = Math.max(fromBeat, seg.startBeat);
    const winEnd = Math.min(toBeat, seg.endBeat);
    if (winStart > winEnd + EPS) {
      continue;
    }
    // Solve cubeBottom(b) = anchorY + anchorVy·dt + ½g·dt² + half = topY.
    const c = state.anchorY + half - seg.topY;
    const disc = state.anchorVy * state.anchorVy - 2 * g * c;
    if (disc < 0) {
      continue;
    }
    const dt = (-state.anchorVy + Math.sqrt(disc)) / g; // descending root
    const landBeat = state.anchorBeat + dt;
    if (landBeat > state.anchorBeat + EPS && landBeat >= winStart - EPS && landBeat <= winEnd + EPS) {
      return {
        type: 'landing',
        beat: Math.max(landBeat, fromBeat),
        surfaceTopY: seg.topY,
        lethal: seg.lethal === true,
      };
    }
  }
  return null;
}

/**
 * Next walk-off for a grounded cube: the end of the current surface segment,
 * if the surface DROPS there (the cube falls off a block's trailing edge).
 * A surface RISE while grounded is a lethal leading face handled by the
 * hazard list, so it returns null. Returns null at the chart end.
 */
function computeNextWalkOff(
  state: JumpEngineState,
  config: JumpEngineConfig,
  fromBeat: number,
  toBeat: number,
): JumpWalkOffEvent | null {
  const segs = config.surface;
  const seg = surfaceSegmentAt(segs, Math.max(fromBeat, state.anchorBeat));
  const edge = seg.endBeat;
  if (edge > toBeat + EPS || edge >= config.chart.totalBeats - EPS) {
    return null;
  }
  const nextTop = surfaceSegmentAt(segs, edge).topY;
  if (nextTop > seg.topY + EPS) {
    return { type: 'walkoff', beat: Math.max(edge, fromBeat) };
  }
  return null;
}

/**
 * Re-anchor the trajectory at `beat`, committing the cube's position and
 * (airborne) velocity. Called only at EVENT beats — inputs, landings,
 * walk-offs, jumps — which are identical across client / server re-sims, so
 * every re-anchor is resolution-independent.
 */
function commitTo(state: JumpEngineState, config: JumpEngineConfig, beat: number): void {
  const y = cubeCentreYAt(state, config, beat);
  const vy = cubeVyAt(state, config, beat);
  state.anchorBeat = beat;
  state.anchorY = y;
  state.anchorVy = state.grounded ? 0 : vy;
  state.cubeY = y;
}

/**
 * Launch the cube off its current surface (must be grounded at `beat`) at
 * `launchVelocity` (upward, so the stored `anchorVy` is its negation). A held
 * press / auto-hop launches at the full {@link JumpEngineConfig.jumpVelocity};
 * a buffered tap launches straight at {@link JumpEngineConfig.shortJumpVelocity}
 * (it has already been released, so there is no upcoming edge to clamp it).
 */
function launchJump(
  state: JumpEngineState,
  beat: number,
  launchVelocity: number,
  auto: boolean,
  events: JumpEngineEvent[],
): void {
  state.grounded = false;
  state.anchorBeat = beat;
  state.anchorVy = -launchVelocity;
  // anchorY already equals the resting centre Y at `beat`; remember it so a
  // mid-ascent release can size the short arc relative to this launch height.
  state.launchCentreY = state.anchorY;
  // Consume any buffered press — this launch satisfies it, so the next landing
  // mustn't re-fire from the same stale `down`.
  state.lastDownBeat = Number.NEGATIVE_INFINITY;
  events.push({ type: 'jump', beat, payload: { cubeY: state.anchorY, auto } });
}

/**
 * Clamp the cube onto the SHORT arc on an early release: if it is still
 * ascending and below the short jump's apex height, reduce its upward speed so
 * its remaining rise tops out exactly `shortJumpVelocity² / (2·gravity)` above
 * the launch surface — Mario-style variable jump height with a crisp, fixed
 * short apex. A no-op once at/above that height (the cube simply stops climbing
 * there) or already descending, so a held press that releases after the apex
 * keeps the full long jump. Re-anchors at `beat`, which the caller has just
 * committed to, so `anchorY`/`anchorVy` describe the live airborne state.
 */
function clampToShortArc(state: JumpEngineState, config: JumpEngineConfig, beat: number): void {
  if (state.grounded || state.anchorVy >= 0) {
    return;
  }
  const shortPeak = (config.shortJumpVelocity * config.shortJumpVelocity) / (2 * config.gravity);
  const heightAboveLaunch = state.launchCentreY - state.anchorY;
  const remainingShortRise = shortPeak - heightAboveLaunch;
  // Target upward speed to crest exactly at the short apex; clamp to 0 once
  // already past it. Never speeds the cube up (the short arc is the slower one).
  const targetUp = remainingShortRise > 0 ? Math.sqrt(2 * config.gravity * remainingShortRise) : 0;
  const targetVy = -targetUp;
  if (targetVy > state.anchorVy + EPS) {
    state.anchorBeat = beat;
    state.anchorVy = targetVy;
  }
}

/**
 * Land the cube on a surface, then relaunch on the same beat if either the
 * button is held (auto-hop) or a fresh `down` was buffered just before
 * touchdown (jump buffering). A held press is an `auto` hop and flies the full
 * LONG arc (the player can still cut it short by releasing mid-ascent); a
 * buffered tap was released before touchdown, so it launches straight onto the
 * SHORT arc — a quick tap reads as a small hop with no late release needed.
 */
function landCube(
  state: JumpEngineState,
  config: JumpEngineConfig,
  beat: number,
  surfaceTopY: number,
  events: JumpEngineEvent[],
): void {
  state.grounded = true;
  state.anchorBeat = beat;
  state.anchorY = surfaceTopY - config.playerHalf;
  state.anchorVy = 0;
  state.cubeY = state.anchorY;
  events.push({ type: 'land', beat, payload: { cubeY: state.cubeY, surfaceTopY } });
  if (state.pressing) {
    launchJump(state, beat, config.jumpVelocity, true, events);
  } else if (beat - state.lastDownBeat <= config.inputBufferBeats + EPS) {
    launchJump(state, beat, config.shortJumpVelocity, false, events);
  }
}

/** Cube walks off a block's trailing edge and starts falling (vy = 0). */
function walkOffCube(state: JumpEngineState, beat: number): void {
  state.grounded = false;
  state.anchorBeat = beat;
  state.anchorVy = 0;
  // anchorY stays at the block-top resting centre; gravity now pulls it down.
}

function applyInputEdge(
  state: JumpEngineState,
  config: JumpEngineConfig,
  beat: number,
  type: JumpEngineInputType,
  events: JumpEngineEvent[],
): void {
  const wantPress = type === 'down';
  const changed = wantPress !== state.pressing;
  state.pressing = wantPress;
  if (changed) {
    events.push({ type: 'edge', beat, payload: { pressing: wantPress, cubeY: state.cubeY } });
  }
  if (wantPress) {
    // Record every press for the landing buffer; a grounded press launches the
    // full LONG arc now (launchJump consumes the buffer); an airborne one is
    // held for the upcoming landing. A held jump can still be cut short by the
    // matching release below.
    state.lastDownBeat = beat;
    if (state.grounded) {
      launchJump(state, beat, config.jumpVelocity, false, events);
    }
  } else {
    // Release while still ascending → cut the jump down to the short arc
    // (variable jump height). A no-op past the short apex or while descending.
    clampToShortArc(state, config, beat);
  }
}

function recordDeath(
  state: JumpEngineState,
  config: JumpEngineConfig,
  beat: number,
  hazardId: string,
  reason: JumpDeathReason,
  events: JumpEngineEvent[],
): void {
  state.cubeY = cubeCentreYAt(state, config, beat);
  state.beat = beat;
  state.alive = false;
  state.deathReason = reason;
  state.deathBeat = beat;
  state.collisionObstacleId = hazardId;
  state.combo = 0;
  events.push({ type: 'death', beat, payload: { obstacleId: hazardId, reason } });
}

/**
 * Evaluate every unpassed hazard whose beat lies in `[fromBeat, toBeat]`,
 * sampling the cube's trajectory at the hazard's exact beat. Clears (scores)
 * or ends the run. Does not re-anchor — hazards are point checks on a single
 * inter-event arc segment. Mutates state; pushes events.
 */
function evaluateHazardsInRange(
  state: JumpEngineState,
  config: JumpEngineConfig,
  fromBeat: number,
  toBeat: number,
  events: JumpEngineEvent[],
): void {
  const hazards = config.hazards;
  for (const hazard of hazards) {
    if (hazard.beat < fromBeat - EPS) {
      continue;
    }
    if (hazard.beat > toBeat + EPS) {
      break;
    }
    if (state.passedHazardIds.has(hazard.id)) {
      continue;
    }
    // Each hazard is a point sampled at its exact `beat` (always >= the anchor
    // here, so the sampler is exact and resolution-independent). Adjacent cluster
    // columns are independent point hazards — one arc clears them all.
    const cubeY = cubeCentreYAt(state, config, hazard.beat);
    // Blocks keep the full body so a solid wall stays solid; spikes + saws
    // forgive a graze (smaller kill box than the visual cube).
    const killHalf = hazard.kind === 'block' ? config.playerHalf : config.spikeHazardHalf;
    let collided: boolean;
    let clearance: number;
    if (hazard.bottomY !== undefined) {
      // Saw: a vertically-bounded lethal region. Die on body overlap; clear by
      // arcing fully above `topY` OR keeping the body fully below `bottomY`.
      collided = cubeY + killHalf > hazard.topY + EPS && cubeY - killHalf < hazard.bottomY - EPS;
      clearance = Math.max(
        hazard.topY - (cubeY + config.playerHalf),
        cubeY - config.playerHalf - hazard.bottomY,
      );
    } else {
      // Spike tip / terrain face: the cube's bottom edge must clear `topY`.
      collided = cubeY + killHalf > hazard.topY + EPS;
      clearance = hazard.topY - (cubeY + config.playerHalf);
    }
    if (collided) {
      recordDeath(state, config, hazard.beat, hazard.id, 'collided', events);
      return;
    }
    state.passedHazardIds.add(hazard.id);
    state.combo += 1;
    if (state.combo > state.maxCombo) {
      state.maxCombo = state.combo;
    }
    const verdict = gradeJumpClearance(clearance);
    if (verdict === 'perfect') {
      state.perfects += 1;
    } else {
      state.goods += 1;
    }
    const baseScore = verdict === 'perfect' ? PERFECT_SCORE : GOOD_SCORE;
    const scoreDelta = Math.round(baseScore * comboMultiplier(state.combo));
    state.score += scoreDelta;
    const resolution: JumpResolution = {
      obstacleId: hazard.id,
      beat: hazard.beat,
      clearance,
      verdict,
    };
    state.resolutions.push(resolution);
    events.push({
      type: 'obstacle_passed',
      beat: hazard.beat,
      payload: { obstacleId: hazard.id, scoreDelta, combo: state.combo, verdict },
    });
  }
}

/**
 * Highest point (minimum centre Y) the cube reaches on its current arc within
 * `[b0, b1]`. The airborne parabola is convex in Y, so its min is the apex when
 * that falls inside the window, else the nearer endpoint. Grounded, the cube is
 * stationary at `anchorY`.
 */
function minCubeCentreYInRange(
  state: JumpEngineState,
  config: JumpEngineConfig,
  b0: number,
  b1: number,
): number {
  if (state.grounded) {
    return state.anchorY;
  }
  let min = Math.min(
    cubeCentreYAt(state, config, b0),
    cubeCentreYAt(state, config, b1),
  );
  const apexBeat = state.anchorBeat - state.anchorVy / config.gravity;
  if (apexBeat > b0 + EPS && apexBeat < b1 - EPS) {
    min = Math.min(min, cubeCentreYAt(state, config, apexBeat));
  }
  return min;
}

/**
 * Beat the ascending cube's centre first reaches `centreThreshold` (its top
 * edge touching a ceiling underside) on the current arc — the resolution-
 * independent contact beat, so a ceiling death lands on the same beat at any
 * step size. Falls back to the anchor beat if the arc never crosses (the caller
 * has already confirmed contact via the min test).
 */
function ceilingContactBeat(
  state: JumpEngineState,
  config: JumpEngineConfig,
  centreThreshold: number,
): number {
  const disc =
    state.anchorVy * state.anchorVy - 2 * config.gravity * (state.anchorY - centreThreshold);
  if (disc < 0) {
    return state.anchorBeat;
  }
  const dt = (-state.anchorVy - Math.sqrt(disc)) / config.gravity;
  return state.anchorBeat + dt;
}

/**
 * Earliest beat in `[fromBeat, toBeat]` at which the cube's top edge cracks a
 * ceiling underside, plus the offending ceiling id — or `null` if none is hit.
 * Ceilings are a pure upper bound: they never score or add combo, so this only
 * REPORTS the death; the caller records it AFTER scoring any spike the cube
 * cleared strictly before the contact (a too-high jump still dies on the
 * overhang, but a spike it had already sailed over earlier in the window keeps
 * its score). The contact beat is solved analytically (`ceilingContactBeat`),
 * and we scan ALL overlapping ceilings for the minimum, so the reported death
 * lands on the same beat at any step size — keeping client/server re-sims (and
 * thus scoring) identical regardless of where step boundaries fall.
 */
function nextCeilingDeath(
  state: JumpEngineState,
  config: JumpEngineConfig,
  fromBeat: number,
  toBeat: number,
): { beat: number; id: string } | null {
  let earliest: { beat: number; id: string } | null = null;
  for (const ceiling of config.ceilings) {
    if (ceiling.startBeat > toBeat + EPS) {
      break;
    }
    if (ceiling.endBeat < fromBeat - EPS) {
      continue;
    }
    const s = Math.max(fromBeat, ceiling.startBeat);
    const e = Math.min(toBeat, ceiling.endBeat);
    if (s > e + EPS) {
      continue;
    }
    // Collision once the forgiven top edge pokes above the underside:
    // `cubeCentreY − ceilingHazardHalf < bottomY` ⇔ `cubeCentreY < threshold`.
    const threshold = ceiling.bottomY + config.ceilingHazardHalf;
    if (minCubeCentreYInRange(state, config, s, e) < threshold - EPS) {
      const contact = ceilingContactBeat(state, config, threshold);
      const deathBeat = Math.min(Math.max(contact, ceiling.startBeat), ceiling.endBeat);
      if (!earliest || deathBeat < earliest.beat) {
        earliest = { beat: deathBeat, id: ceiling.id };
      }
    }
  }
  return earliest;
}

export function step(input: JumpStepInput): JumpStepOutput {
  const { config, fromBeat, toBeat } = input;
  const state = input.state;
  const events: JumpEngineEvent[] = [];

  if (!state.alive || state.completed) {
    return { state, events };
  }

  const sortedInputs = [...input.inputs]
    .filter((i) => i.beat >= fromBeat - EPS && i.beat <= toBeat + EPS)
    .sort((a, b) => a.beat - b.beat);
  let ii = 0;
  let cursor = Math.max(fromBeat, state.beat);
  while (ii < sortedInputs.length && sortedInputs[ii].beat < cursor - EPS) {
    ii += 1;
  }

  let guard = 0;
  for (;;) {
    guard += 1;
    if (guard > JUMP_MAX_STEP_EVENTS) {
      console.warn(
        `[jump] step exceeded ${JUMP_MAX_STEP_EVENTS} events in [${fromBeat}, ${toBeat}] — bailing to avoid a loop`,
      );
      break;
    }

    const nextInputBeat = ii < sortedInputs.length ? sortedInputs[ii].beat : Infinity;
    const physics = state.grounded
      ? computeNextWalkOff(state, config, cursor, toBeat)
      : computeNextLanding(state, config, cursor, toBeat);
    const nextPhysicsBeat = physics ? physics.beat : Infinity;
    const eventBeat = Math.min(nextInputBeat, nextPhysicsBeat, toBeat);

    // Resolve a ceiling crack analytically first, but record it only AFTER
    // scoring hazards up to its contact beat: spikes the cube cleared on the way
    // up still count, then the overhang death is recorded (the window is trimmed
    // to `contact − EPS`). Point-checked hazards + an analytic contact beat make
    // this granularity-independent.
    const ceilingDeath = nextCeilingDeath(state, config, cursor, eventBeat);
    const hazardTo = ceilingDeath ? Math.min(eventBeat, ceilingDeath.beat - EPS) : eventBeat;
    evaluateHazardsInRange(state, config, cursor, hazardTo, events);
    if (!state.alive) {
      return { state, events };
    }
    if (ceilingDeath) {
      recordDeath(state, config, ceilingDeath.beat, ceilingDeath.id, 'collided', events);
      return { state, events };
    }

    if (nextInputBeat > toBeat + EPS && nextPhysicsBeat > toBeat + EPS) {
      // No event strictly within the window: sample the arc at toBeat WITHOUT
      // re-anchoring (keeps the anchor on the last event, so client/server
      // sampling stays identical), then stop.
      state.cubeY = cubeCentreYAt(state, config, toBeat);
      state.beat = toBeat;
      break;
    }

    commitTo(state, config, eventBeat);
    state.beat = eventBeat;

    while (ii < sortedInputs.length && sortedInputs[ii].beat <= eventBeat + EPS) {
      applyInputEdge(state, config, eventBeat, sortedInputs[ii].type, events);
      ii += 1;
    }
    if (physics && Math.abs(physics.beat - eventBeat) < 1e-6) {
      if (physics.type === 'landing' && !state.grounded) {
        if (physics.lethal) {
          // The "surface" here is a pit floor — the cube fell into a chasm.
          recordDeath(state, config, eventBeat, `pit@${eventBeat.toFixed(3)}`, 'fell', events);
          return { state, events };
        }
        landCube(state, config, eventBeat, physics.surfaceTopY, events);
      } else if (physics.type === 'walkoff' && state.grounded) {
        walkOffCube(state, eventBeat);
      }
    }
    cursor = eventBeat;
  }

  if (state.beat >= config.chart.totalBeats - EPS && !state.completed) {
    state.completed = true;
    events.push({ type: 'completed', beat: state.beat });
  }

  return { state, events };
}

export function summariseJumpAccuracy(state: JumpEngineState): number {
  return computeAccuracy(state.perfects, state.goods, state.oks, state.misses);
}
